/**
 * Utilidades para trabajar con badges del botón
 */

import { BadgeColor, BadgeSize } from '@/shared/types/badge';
import { ButtonColors, IconType, AnyIconDefinition } from '@/shared/types/types';

/**
 * Determina si el badge debe mostrarse
 * El badge se muestra si tiene label (incluyendo "0"), tiene icono, está en loading, o es dot
 *
 * @param badgeColor - Color del badge
 * @param badgeLabel - Label del badge
 * @param badgeIcon - Icono del badge
 * @param badgeSize - Tamaño del badge
 * @param badgeLoading - Si el badge está en estado de carga
 * @returns true si el badge debe mostrarse
 */
export const shouldShowBadge = (
  badgeColor?: BadgeColor,
  badgeLabel?: string,
  badgeIcon?: IconType | AnyIconDefinition | string,
  badgeSize?: BadgeSize,
  badgeLoading?: boolean,
): boolean => {
  // Si está en modo loading, siempre mostrar (incluso sin badgeColor)
  if (badgeLoading) {
    return true;
  }

  if (!badgeColor) {
    return false;
  }

  // Si tiene icono, siempre mostrar
  if (badgeIcon) {
    return true;
  }

  // Si tiene label (incluyendo "0"), mostrar
  // Si label es undefined, null, o string vacío, no mostrar
  if (badgeLabel !== null && badgeLabel !== '') {
    return true;
  }

  // Si es size='dot', no necesita label ni icon
  if (badgeSize && badgeSize === 'dot') {
    return true;
  }

  return false;
};

/**
 * Determina si el badge debe tener borde automáticamente
 * Basado en el diseño de Figma: el borde se necesita para contrastar con el fondo del botón
 *
 * @param badgeStroke - Valor explícito de stroke (tiene prioridad)
 * @param badgeColor - Color del badge
 * @param buttonColor - Color del botón
 * @returns true si el badge debe tener borde
 */
export const shouldBadgeHaveStroke = (
  badgeStroke: boolean | undefined,
  badgeColor: BadgeColor | undefined,
  buttonColor: ButtonColors | undefined,
): boolean => {
  // Si badgeStroke está explícitamente definido, usar ese valor
  if (badgeStroke !== undefined) {
    return badgeStroke;
  }

  // Si no hay badgeColor, no aplicar borde
  if (!badgeColor) {
    return false;
  }

  // Obtener el color del botón (usar 'primary' como default si no está definido)
  const color = buttonColor || 'primary';

  // Lógica automática basada en el color del botón y el badge
  if (badgeColor === 'accent') {
    // Badge accent necesita borde en botones primary para contrastar con el fondo azul
    return color === 'primary';
  }

  if (badgeColor === 'gray') {
    // Badge gray necesita borde en botones secondary y tertiary para contrastar con fondos claros
    return color === 'secondary' || color === 'tertiary';
  }

  // Para otros colores de badge, no usar borde por defecto
  return false;
};

/**
 * Obtiene el tamaño efectivo del badge
 * Si está en loading y no tiene tamaño definido, usa 'sm' por defecto
 *
 * @param badgeLoading - Si el badge está en estado de carga
 * @param badgeSize - Tamaño del badge
 * @returns Tamaño efectivo del badge
 */
export const getEffectiveBadgeSize = (
  badgeLoading: boolean | undefined,
  badgeSize: BadgeSize | undefined,
): BadgeSize => {
  return badgeLoading && !badgeSize ? 'sm' : badgeSize || 'sm';
};
